// Discussion Forum Page - Facebook/WhatsApp Style Interface
// Date: January 8, 2026
// Purpose: Redesigned discussion interface with threaded comments and inline replies

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

interface Discussion {
  id: string;
  title: string;
  content: string;
  category: string;
  status: 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
  is_pinned: boolean;
  is_locked: boolean;
  view_count: number;
  like_count: number;
  reply_count: number;
  last_activity_at: string;
  created_at: string;
  created_by: string;
  author_name: string;
  tags: string[];
  mentioned_entities: any[];
}

interface DiscussionReply {
  id: string;
  discussion_id: string;
  content: string;
  created_at: string;
  created_by: string;
  author_name: string;
  like_count: number;
  is_liked: boolean;
  parent_reply_id?: string;
  replies?: DiscussionReply[];
}

export const DiscussionForumPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState<Discussion | null>(null);
  const [replies, setReplies] = useState<DiscussionReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ discussionId: string; parentReplyId?: string } | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const categories = [
    { value: 'ALL', label: 'All Discussions' },
    { value: 'VISION', label: 'Visions' },
    { value: 'OBJECTIVE', label: 'Objectives' },
    { value: 'STRATEGY', label: 'Strategies' },
    { value: 'PROJECT', label: 'Projects' },
    { value: 'INCHSTONE', label: 'Inchstones' },
    { value: 'RISK', label: 'Risks' },
    { value: 'KPI', label: 'KPIs' },
    { value: 'TRANSFORMATION', label: 'Transformations' },
    { value: 'GENERAL', label: 'General' }
  ];

  useEffect(() => {
    fetchDiscussions();
  }, []);

  // Check URL for discussion ID and open it when discussions are loaded
  useEffect(() => {
    const discussionId = searchParams.get('id');
    if (discussionId && discussions.length > 0 && !selectedDiscussion) {
      const discussion = discussions.find(d => d.id === discussionId);
      if (discussion) {
        handleSelectDiscussion(discussion);
      }
    }
  }, [discussions, searchParams]);

  useEffect(() => {
    if (selectedDiscussion) {
      fetchDiscussionReplies(selectedDiscussion.id);
      // Update URL with discussion ID
      setSearchParams({ id: selectedDiscussion.id });
      // Scroll to bottom when replies load
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [selectedDiscussion]);

  useEffect(() => {
    // Auto-focus reply input when replying
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  const fetchDiscussions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/discussions');
      setDiscussions(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching discussions:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch discussions');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscussionReplies = async (discussionId: string) => {
    try {
      const response = await api.get(`/discussions/${discussionId}/replies`);
      const flatReplies = response.data.data || [];
      // Build threaded structure
      const threadedReplies = buildThreadedReplies(flatReplies);
      setReplies(threadedReplies);
    } catch (error: any) {
      console.error('Error fetching replies:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch replies');
    }
  };

  const buildThreadedReplies = (flatReplies: DiscussionReply[]): DiscussionReply[] => {
    const replyMap = new Map<string, DiscussionReply>();
    const rootReplies: DiscussionReply[] = [];

    // First pass: create map of all replies
    flatReplies.forEach(reply => {
      replyMap.set(reply.id, { ...reply, replies: [] });
    });

    // Second pass: build tree structure
    flatReplies.forEach(reply => {
      const replyNode = replyMap.get(reply.id)!;
      if (reply.parent_reply_id) {
        const parent = replyMap.get(reply.parent_reply_id);
        if (parent) {
          if (!parent.replies) parent.replies = [];
          parent.replies.push(replyNode);
        }
      } else {
        rootReplies.push(replyNode);
      }
    });

    return rootReplies;
  };

  const handleCreateReply = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!replyingTo || !replyContent.trim()) return;

    try {
      setSendingReply(true);
      const payload: any = {
        content: replyContent.trim()
      };

      if (replyingTo.parentReplyId) {
        payload.parent_reply_id = replyingTo.parentReplyId;
      }

      await api.post(`/discussions/${replyingTo.discussionId}/replies`, payload);
      
      // Refresh replies
      await fetchDiscussionReplies(replyingTo.discussionId);
      
      // Clear reply state
      setReplyContent('');
      setReplyingTo(null);
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error: any) {
      console.error('Error creating reply:', error);
      setError(error.response?.data?.message || error.message || 'Failed to create reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleLikeReply = async (replyId: string, discussionId: string) => {
    try {
      // Toggle like (you may need to implement this endpoint)
      await api.post(`/discussions/${discussionId}/replies/${replyId}/like`);
      await fetchDiscussionReplies(discussionId);
    } catch (error: any) {
      console.error('Error liking reply:', error);
    }
  };

  const handleSelectDiscussion = async (discussion: Discussion) => {
    setSelectedDiscussion(discussion);
    setReplyingTo({ discussionId: discussion.id });
    // Update URL with discussion ID
    setSearchParams({ id: discussion.id });
    // Increment view count
    try {
      await api.post(`/discussions/${discussion.id}/view`);
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const handleCloseDiscussion = () => {
    setSelectedDiscussion(null);
    setReplyingTo(null);
    setSearchParams({}); // Clear URL params
  };

  const filteredDiscussions = discussions.filter(discussion => {
    const matchesSearch = discussion.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         discussion.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'ALL' || discussion.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderReply = (reply: DiscussionReply, discussionId: string, depth: number = 0) => {
    return (
      <div key={reply.id} className={`${depth > 0 ? 'ml-8 mt-3' : 'mt-4'} border-l-2 border-gray-200 pl-4`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
            {reply.author_name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-sm text-gray-900">{reply.author_name}</span>
              <span className="text-xs text-gray-500">{formatTime(reply.created_at)}</span>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">{reply.content}</p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleLikeReply(reply.id, discussionId)}
                className="flex items-center gap-1 text-xs text-gray-600 hover:text-red-600 transition-colors"
              >
                {reply.is_liked ? (
                  <HeartSolidIcon className="w-4 h-4 text-red-600" />
                ) : (
                  <HeartIcon className="w-4 h-4" />
                )}
                <span>{reply.like_count || 0}</span>
              </button>
              <button
                onClick={() => setReplyingTo({ discussionId, parentReplyId: reply.id })}
                className="text-xs text-gray-600 hover:text-blue-600 transition-colors"
              >
                Reply
              </button>
            </div>
            {/* Nested replies */}
            {reply.replies && reply.replies.length > 0 && (
              <div className="mt-3">
                {reply.replies.map(nestedReply => renderReply(nestedReply, discussionId, depth + 1))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading discussions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Discussion Forum</h1>
            <p className="text-sm text-gray-600 mt-1">Collaborate and discuss transformations, strategies, and projects</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            New Discussion
          </button>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search discussions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
          {/* Discussions List */}
          <div className={`lg:col-span-1 transition-all duration-300 ${selectedDiscussion ? 'lg:col-span-1' : 'lg:col-span-3'}`}>
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Discussions</h2>
              </div>
              <div className="divide-y divide-gray-200 max-h-[calc(100vh-300px)] overflow-y-auto">
                {filteredDiscussions.length === 0 ? (
                  <div className="p-8 text-center">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">No discussions found</p>
                  </div>
                ) : (
                  filteredDiscussions.map((discussion) => (
                    <div
                      key={discussion.id}
                      onClick={() => handleSelectDiscussion(discussion)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedDiscussion?.id === discussion.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm flex-1">{discussion.title}</h3>
                        {discussion.is_pinned && (
                          <span className="text-xs text-blue-600 font-medium">Pinned</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2 mb-2">{discussion.content}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{discussion.author_name}</span>
                        <span>•</span>
                        <span>{discussion.reply_count} replies</span>
                        <span>•</span>
                        <span>{formatTime(discussion.last_activity_at)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Discussion Detail Side Panel - Slides in from right */}
          {selectedDiscussion && (
            <>
                    {/* Backdrop for mobile */}
                    <div 
                      className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                      onClick={handleCloseDiscussion}
                    />
              <div className="lg:col-span-2 fixed lg:relative inset-y-0 right-0 lg:right-auto w-full lg:w-auto bg-white shadow-xl lg:shadow-sm rounded-lg lg:rounded-lg z-50 lg:z-auto flex flex-col h-screen lg:h-[calc(100vh-200px)] animate-in slide-in-from-right duration-300">
              {/* Discussion Header */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-start justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900">{selectedDiscussion.title}</h2>
                  <div className="flex items-center gap-2">
                    <button className="text-gray-400 hover:text-gray-600">
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </button>
                            <button
                              onClick={handleCloseDiscussion}
                              className="lg:hidden text-gray-400 hover:text-gray-600 p-1"
                            >
                              <XMarkIcon className="h-6 w-6" />
                            </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedDiscussion.author_name}</span>
                  <span>•</span>
                  <span>{formatTime(selectedDiscussion.created_at)}</span>
                </div>
              </div>

              {/* Discussion Content */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedDiscussion.content}</p>
              </div>

              {/* Replies Container - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {replies.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No replies yet. Be the first to comment!</p>
                  </div>
                ) : (
                  replies.map(reply => renderReply(reply, selectedDiscussion.id))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input - Fixed at Bottom */}
              <div className="p-4 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                {replyingTo && replyingTo.discussionId === selectedDiscussion.id && (
                  <form onSubmit={handleCreateReply} className="flex items-end gap-2">
                    <div className="flex-1">
                      <textarea
                        ref={replyInputRef}
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder={replyingTo.parentReplyId ? "Write a reply..." : "Write a comment..."}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={replyingTo.parentReplyId ? 2 : 3}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            handleCreateReply();
                          }
                        }}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!replyContent.trim() || sendingReply}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                      {sendingReply ? 'Sending...' : 'Send'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </>
          )}
        </div>

        {/* Create Discussion Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Start New Discussion</h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                {/* Create discussion form would go here */}
                <p className="text-gray-600">Create discussion form implementation needed</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
